import { captureException } from '@sentry/nextjs';
import classNames from 'classnames';
import { Form as FormikForm, Formik } from 'formik';
import { useRouter } from 'next/router';
import React from 'react';

import { SubmitButton } from '@components/Form/SubmitButton/SubmitButton';
import { ThankYou } from '@components/Form/ThankYou/ThankYou';
import { ScrollHere } from '@containers/ScrollTo/ScrollHere';
import type { InquiryTarget } from '@hooks/api/useSendInquiryMutation';
import { useSendInquiryMutation } from '@hooks/api/useSendInquiryMutation';

export type IButtonPosition = 'start' | 'center';

export type InquiryValues = {
  name: string;
  email: string;
  message: string;
  website: string;
  estimatedTraffic: string;
};

interface Props {
  initialValues: InquiryValues;
  target: InquiryTarget;
  buttonText: string;
  id?: string;
  subject: string;
  buttonPosition?: IButtonPosition;
  isScrollToErrorEnabled?: boolean;
  customThankYouComponent?: JSX.Element | null;
  errorPosition?: 'top' | 'right';
  buttonWidth?: 'full' | 'auto';
  onSuccess?: (values: InquiryValues) => void;
  children: React.ReactNode;
}

export const Form = ({
  initialValues,
  id,
  subject,
  buttonText,
  buttonPosition = 'start',
  isScrollToErrorEnabled = true,
  customThankYouComponent,
  errorPosition,
  target,
  buttonWidth = 'auto',
  onSuccess,
  children,
}: React.PropsWithChildren<Props>) => {
  const [isLoading, setIsLoading] = React.useState(false);
  const router = useRouter();

  const sendInquiryMutation = useSendInquiryMutation();

  const getUtmParameters = React.useCallback(() => {
    const supportedUtmParameters = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_term',
      'utm_content',
    ];
    return Object.entries(router.query).reduce<Record<string, unknown>>(
      (parameters, [key, value]) =>
        supportedUtmParameters.includes(key)
          ? {
              ...parameters,
              [key.replace('utm_', '')]: value,
            }
          : parameters,
      {},
    );
  }, [router.query]);

  const startTimeout = () => {
    setIsLoading(true);

    setTimeout(() => {
      setIsLoading(false);
    }, 1000);
  };

  if (sendInquiryMutation.isSuccess) {
    return (
      <>
        {!isScrollToErrorEnabled && <ScrollHere />}
        {customThankYouComponent ?? <ThankYou />}
      </>
    );
  }

  return (
    <Formik<InquiryValues>
      initialValues={initialValues}
      onSubmit={(values, { setStatus, setSubmitting, resetForm }) => {
        startTimeout();
        setSubmitting(true);
        sendInquiryMutation.mutate(
          {
            email: values.email,
            body: {
              email: values.email,
              name: values.name,
              subject: `CDN77.com - ${subject}`,
              message: values.message,
              estimated_traffic: values.estimatedTraffic || undefined,
              project: values.website || undefined,
              formUrl: router.asPath,
              utm: getUtmParameters(),
            },
            department: target,
            wherefrom: `Contact - ${target}`,
          },
          {
            onSuccess: () => {
              onSuccess?.(values);
              resetForm();
              setSubmitting(false);
            },
            onError: error => {
              captureException(error);
              setStatus({
                message: 'Request failed',
              });
              setSubmitting(false);
            },
          },
        );
      }}
    >
      {({ status }) => (
        <FormikForm id={id} method="post" noValidate>
          {children}
          <div
            className={
              buttonWidth === 'auto'
                ? classNames('d-flex', `justify-content-${buttonPosition}`)
                : undefined
            }
          >
            <SubmitButton
              error={status?.message}
              errorPosition={errorPosition}
              failedAttempts={sendInquiryMutation.failureCount}
              id={`${id ?? 'contact-form'}-submit-button`}
              isFullWidth={buttonWidth === 'full'}
              isLoading={isLoading}
            >
              {buttonText}
            </SubmitButton>
          </div>
        </FormikForm>
      )}
    </Formik>
  );
};
